import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, Lock, Calculator, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Seo } from "../components/Seo";
import { Faq } from "../components/Faq";
import { CheckoutDialog } from "../components/CheckoutDialog";
import { useAuth } from "../context/AuthContext";

const PRICES = { monthly: 699, annual: 6900 };
const usd = (c) => (c % 100 === 0 ? `A$${c / 100}` : `A$${(c / 100).toFixed(2)}`);
const annualPerMonth = `A$${(PRICES.annual / 12 / 100).toFixed(2)}`;

const SUBSCRIBE_FEATURES = [
  "Everything in the free trial, forever",
  "Permanent, unlimited saved projects",
  "Search & reload your project library",
  "Duplicate any saved project",
  "Materials cost calculator",
  "Printable & exportable cut sheets",
];

const PLAN_FAQS = [
  { q: "How does the free trial work?", a: "Just sign in with Google or email — no card required — and you get 7 days of full access: the complete calculator, printable and exportable cut sheets, and temporary project saves. We'll email you a reminder on day 5, day 6 and your last day. If you don't subscribe, your trial account is closed and its saved projects removed when the trial ends." },
  { q: "Can I use the calculator without signing in?", a: "Yes. Anyone can use the live calculator and see visual and cut-size results. After a few adjustments we'll invite you to start your free trial to keep refining, save your work and export cut sheets." },
  { q: "What do I get when I subscribe?", a: "A permanent, unlimited project library with search and duplicate, the materials cost calculator, and full print/export — all retained from your trial." },
  { q: "Monthly or annual — which should I pick?", a: `Monthly is ${usd(PRICES.monthly)}/month and great to start. Annual is ${usd(PRICES.annual)}/year — that's ${annualPerMonth}/month, our best value for regular framers and studios.` },
  { q: "Can I cancel anytime?", a: "Yes. Your subscription renews automatically and you can cancel whenever you like — you keep access until the end of the paid period." },
  { q: "What currency are prices in?", a: "Prices are in Australian dollars (AUD) and processed securely through Square. If your card is in another currency, your bank converts the amount at its own rate." },
];

export default function Pricing() {
  const { user, isSubscribed } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("monthly");
  const [checkout, setCheckout] = useState({ open: false, cycle: null, price: 0 });

  const startCheckout = () => {
    if (!user) { navigate("/login", { state: { from: "/pricing" } }); return; }
    setCheckout({ open: true, cycle, price: PRICES[cycle] });
  };

  const price = PRICES[cycle];

  return (
    <>
      <Seo title="Pricing & Plans" path="/pricing" faq={PLAN_FAQS}
        description="Framer's Bench pricing — start a free 7-day trial, no card required. Subscribe for A$6.99/month or A$69/year for unlimited saved projects and the materials cost calculator." />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <Badge variant="secondary" className="mb-4 gap-1.5"><Clock className="h-3.5 w-3.5" /> 7-day free trial · no card required</Badge>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Stop wasting matboard and mouldings.</h1>
          <p className="mt-4 text-lg font-semibold text-primary">Get perfect cuts the first time - one simple plan.</p>
          <p className="mt-3 text-muted-foreground">Use the calculator free. Sign in to start your 7-day trial, then subscribe to keep your library, unlock the materials cost calculator and export cut sheets.</p>
        </div>

        <div className="mx-auto mt-8 flex max-w-3xl items-start gap-3 rounded-2xl border border-primary/25 bg-primary/5 p-5" data-testid="pricing-roi-banner">
          <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className="text-[15px] leading-relaxed text-foreground/85">
            <strong>Framing materials are expensive.</strong> A single spoiled sheet of matboard or a mis-cut length of moulding
            can cost more than a month's subscription. If you avoid just ONE mistake, Framer's Bench has paid for itself.
          </p>
        </div>

        {/* How it works */}
        <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-4 md:grid-cols-3" data-testid="how-it-works">
          {[
            { icon: Calculator, title: "1 · Try it free", body: "Use the live calculator and see visual + cut-size results. No account needed to explore." },
            { icon: Clock, title: "2 · Start your trial", body: "Sign in (Google or email) for 7 days of full access — export, print and save. No card required." },
            { icon: Sparkles, title: "3 · Subscribe", body: "Keep your saved projects forever, unlock materials costing and duplication." },
          ].map((s) => (
            <div key={s.title} className="rounded-2xl border border-border bg-card p-5">
              <s.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading text-base font-bold">{s.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{s.body}</p>
            </div>
          ))}
        </div>

        {/* Billing toggle */}
        <div className="mt-14 flex items-center justify-center">
          <div className="inline-flex rounded-lg border border-border bg-secondary p-1" data-testid="billing-toggle">
            {[{ v: "monthly", l: "Monthly" }, { v: "annual", l: "Annual" }].map((o) => (
              <button key={o.v} data-testid={`billing-${o.v}`} onClick={() => setCycle(o.v)}
                className={`rounded-md px-5 py-2 text-sm font-semibold transition-colors ${cycle === o.v ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {o.l}{o.v === "annual" && <span className="ml-1.5 text-xs font-medium text-primary">2 months free</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Plan card */}
        <div className="mx-auto mt-10 max-w-md">
          <div className="relative flex flex-col rounded-2xl border border-primary bg-card p-7 shadow-lg ring-1 ring-primary" data-testid="plan-card-subscription">
            <Badge className="absolute -top-3 left-7" data-testid="plan-badge">Framer's Bench</Badge>
            <h3 className="font-heading text-xl font-extrabold">Subscription</h3>
            <p className="mt-1 text-sm text-muted-foreground">Every calculation on one single, easy-to-read page - unlocked and permanent.</p>
            <div className="mt-5 flex items-end gap-1">
              <span className="font-heading text-5xl font-extrabold tracking-tight">{usd(price)}</span>
              <span className="mb-1.5 text-sm text-muted-foreground">{cycle === "annual" ? "/year" : "/month"}</span>
            </div>
            {cycle === "annual" && <p className="mt-1 text-xs font-medium text-primary">Just {annualPerMonth}/month, billed yearly</p>}
            <ul className="mt-6 flex-1 space-y-3">
              {SUBSCRIBE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-[15px]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> <span>{f}</span>
                </li>
              ))}
            </ul>
            {isSubscribed ? (
              <Button className="mt-7 w-full" disabled data-testid="plan-cta-current">You're subscribed</Button>
            ) : (
              <Button className="mt-7 w-full" onClick={startCheckout} data-testid="plan-cta-subscribe">
                {user ? "Subscribe now" : "Start free trial"}
              </Button>
            )}
            <p className="mt-3 text-center text-xs text-muted-foreground">
              {user ? "Cancel anytime · secured by Square" : "No card required to start · cancel anytime"}
            </p>
          </div>
        </div>

        {/* Trial vs subscription */}
        <div className="mx-auto mt-16 max-w-3xl overflow-hidden rounded-2xl border border-border">
          <table className="w-full text-sm" data-testid="comparison-table">
            <thead>
              <tr className="border-b border-border bg-secondary/60">
                <th className="p-4 text-left font-heading font-bold">What you get</th>
                <th className="p-4 text-center font-heading font-bold">Free trial<br /><span className="text-xs font-normal text-muted-foreground">7 days</span></th>
                <th className="p-4 text-center font-heading font-bold text-primary">Subscription</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: "Full calculator, visuals & cut sizes", trial: true, sub: true },
                { label: "Print & export cut sheets", trial: true, sub: true },
                { label: "Save projects", trial: "Temporary", sub: "Permanent" },
                { label: "Project library (search & reload)", trial: false, sub: true },
                { label: "Duplicate projects", trial: false, sub: true },
                { label: "Materials cost calculator", trial: false, sub: true },
              ].map((row) => (
                <tr key={row.label} className="border-b border-border last:border-0">
                  <td className="p-4 text-foreground/90">{row.label}</td>
                  {["trial", "sub"].map((k) => (
                    <td key={k} className="p-4 text-center">
                      {row[k] === true ? <Check className="mx-auto h-4 w-4 text-primary" />
                        : row[k] === false ? <Lock className="mx-auto h-3.5 w-3.5 text-muted-foreground/40" />
                        : <span className="text-xs font-semibold text-muted-foreground">{row[k]}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Faq items={PLAN_FAQS} title="Pricing & Plan questions" />

      <CheckoutDialog
        open={checkout.open}
        onOpenChange={(o) => setCheckout((c) => ({ ...c, open: o }))}
        cycle={checkout.cycle} price={checkout.price}
        onSuccess={() => navigate("/calculator")}
      />
    </>
  );
}
